import type { ReactNode } from 'react';
import { useUserLoggedIn } from '~/modules/user/hook/use-user';
import { DirectoryIntro } from './DirectoryIntro';
import { DirectoryPromoBanner } from './DirectoryPromoBanner';

/** A full-width module injected into the directory grid at a fixed cadence. */
export interface DirectoryGridInjection {
    key: string;
    node: ReactNode;
    /**
     * Show at the first injection point only, instead of cycling back round.
     * For modules that make their point once and become noise on repeat.
     */
    once?: boolean;
}

/**
 * The modules injected into the directory grid, cycled in order at the grid's
 * injection cadence. Extend this list to add future injectable modules (e.g.
 * ads).
 *
 * Both modules cycle, so a logged-out reader meets them in turn — Get Matched,
 * sign in, Get Matched — rather than the same prompt at every break. A signed-in
 * reader has nothing to sign in for, so the sign-in module is not in the list at
 * all and Get Matched takes every slot on its own.
 */
export const useDirectoryGridInjections = (): DirectoryGridInjection[] => {
    const { isLoggedIn } = useUserLoggedIn();

    const injections: DirectoryGridInjection[] = [
        {
            key: 'get-matched',
            node: (
                <DirectoryIntro
                    variant="grid-injection"
                    eyebrow="Find the perfect solution"
                    heading="Can't find what you're after? Get Matched lets you describe what you need and have matching suppliers come to you."
                />
            ),
        },
    ];

    if (!isLoggedIn) {
        injections.push({ key: 'unlock', node: <DirectoryPromoBanner /> });
    }

    return injections;
};
